import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { receivingLotService } from '@/services/receiving-lot.service';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';

interface ReopenLotDialogProps {
  lotId: string | null;
  onClose: () => void;
  onReopened: () => void;
}

/**
 * Reopen requires a non-empty reason (Owner Decision 1, D-056 addendum) —
 * mirrors ReturnToEngineerDialog's mandatory-reason pattern exactly. The
 * server enforces the actual authorization (RECEIVING_COMPLETE reopen vs.
 * the stricter CLOSED reopen); this dialog does not attempt to duplicate
 * that logic client-side — a 403 surfaces as a toast error like any other.
 */
export function ReopenLotDialog({ lotId, onClose, onReopened }: ReopenLotDialogProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const reopenMutation = useMutation({
    mutationFn: (reason: string) => receivingLotService.reopen(lotId!, reason),
    onSuccess: () => {
      toast.success('Receiving lot reopened');
      queryClient.invalidateQueries({ queryKey: ['receiving-lot', lotId] });
      setReason('');
      onReopened();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to reopen receiving lot')),
  });

  const handleClose = () => {
    if (reopenMutation.isPending) return;
    setReason('');
    onClose();
  };

  return (
    <Modal open={lotId !== null} onClose={handleClose} title="Reopen Receiving Lot">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Reopening allows a new Arrival to be recorded for a late physical delivery. This action is
          audited and requires a reason.
        </p>
        <FormField label="Reason for reopening" htmlFor="reopen-lot-reason" required>
          <Textarea
            id="reopen-lot-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Sixth courier package arrived two days after the initial delivery…"
            rows={4}
            maxLength={2000}
            disabled={reopenMutation.isPending}
          />
        </FormField>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={handleClose} disabled={reopenMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => reopenMutation.mutate(reason)}
            disabled={!reason.trim() || reopenMutation.isPending}
          >
            {reopenMutation.isPending && (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
            )}
            Reopen Lot
          </Button>
        </div>
      </div>
    </Modal>
  );
}
